import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { addSales } from "../../SalesService";
import { buildInvoicePayloadWithTax, calculateCartTotals } from "../utils/taxCalculations";
import { getDefaultCurrency } from "../utils/salesCalculations";
import buildSalesFormData from "../utils/commonUtils";
import { isOverpayment } from "../../../../utils/financeEngine";
import { createPharmaDoctorAPI } from "../../../PharmaDoctors/PharmaDoctorService";
import {
    EMPTY_DOCTOR_FORM,
    buildOptionalSaleMeta,
    buildInvoiceDoctorInfo,
    buildDoctorApiPayload,
    buildDoctorSelectOption,
    extractCreatedDoctor,
    extractCreatedDoctorId,
    validateDoctorFormForCreate,
    isPlaceholderDoctorName,
    sanitizeDoctorName,
} from "../utils/doctorFormUtils";
import {
    resolveDoctorFromPrescription,
    resolveDoctorIdForSale,
} from "../utils/doctorResolution";

const INITIAL_PAYMENT_DATA = (pharmacyPOS) => ({
    currency: getDefaultCurrency(pharmacyPOS),
    type: "cash",
    amount: "",
    patientName: "",
    partialPaid: null,
    isSettle: false,
    notes: "",
    zigConversionValue: 1,
});

// Helper function to determine tax type
export const determineTaxType = (pharmacyCode, patientCode) => {
    // If either is missing or empty, use SGST/CGST (intra-state)
    if (!pharmacyCode || pharmacyCode.trim() === "" || !patientCode || patientCode.trim() === "") {
        return true; // Use SGST/CGST
    }

    // If both are available, compare them
    return pharmacyCode === patientCode;
};

export const usePaymentManager = ({
    t,
    cart,
    itemTaxes,
    discountType,
    discountValue,
    transportCost,
    customerType,
    selectedPatient,
    prescriptionData,
    pharmacyPOS,
    getCurrencySymbol,
    setLoading,
    convertCurrency,
    currency,
    posTaxType,
    pharmacyStateCode,
    patientStateCode,
    prescriptionFile
}) => {
    const [paymentData, setPaymentData] = useState(
        INITIAL_PAYMENT_DATA(pharmacyPOS)
    );
    const [paidType, setPaidType] = useState("full");
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showInvoice, setShowInvoice] = useState(false);
    const [invoiceData, setInvoiceData] = useState(null);
    const [errors, setErrors] = useState({});
    const [prescriptionDate, setPrescriptionDate] = useState("");
    const [doctorForm, setDoctorForm] = useState(EMPTY_DOCTOR_FORM);
    const [doctorId, setDoctorId] = useState(null);
    const [selectedDoctorOption, setSelectedDoctorOption] = useState(null);
    const [addingDoctor, setAddingDoctor] = useState(false);
    const [doctorFormVersion, setDoctorFormVersion] = useState(0);

    const resetDoctorPrescriptionMeta = useCallback(() => {
        setPrescriptionDate("");
        setDoctorForm(EMPTY_DOCTOR_FORM);
        setDoctorId(null);
        setSelectedDoctorOption(null);
        setAddingDoctor(false);
        setDoctorFormVersion((prev) => prev + 1);
    }, []);

    const clearDoctorSelection = useCallback(() => {
        setDoctorForm(EMPTY_DOCTOR_FORM);
        setDoctorId(null);
        setSelectedDoctorOption(null);
        setDoctorFormVersion((prev) => prev + 1);
        setErrors((prev) => {
            const next = { ...prev };
            delete next.doctorName;
            delete next.doctorEmail;
            return next;
        });
    }, []);

    const updateDoctorForm = useCallback((patch) => {
        setDoctorForm((prev) => ({ ...prev, ...patch }));
        if (patch && Object.keys(patch).some((key) => key !== "note" && patch[key] !== undefined)) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next.doctorName;
                delete next.doctorEmail;
                return next;
            });
        }
    }, []);

    // Determine if we should use intra-state (SGST/CGST) or inter-state (IGST)
    const isSameState = useMemo(() => {
        return determineTaxType(pharmacyStateCode, patientStateCode);
    }, [pharmacyStateCode, patientStateCode]);

    // Calculate totals using the tax calculations
    const totals = useMemo(() => {
        return calculateCartTotals(
            cart,
            itemTaxes,
            discountType,
            discountValue,
            transportCost,
            posTaxType,
            isSameState
        );
    }, [cart, itemTaxes, discountType, discountValue, transportCost, posTaxType, isSameState]);

    const openPaymentModal = useCallback(
        ({ customerName, amount }) => {
            setPaymentData((prev) => ({
                ...prev,
                currency: getDefaultCurrency(pharmacyPOS),
                patientName: customerName || prev.patientName,
                amount: amount?.toFixed(3) ?? "",
            }));
            setPaidType("full");
            setErrors({});
            setShowPaymentModal(true);
        },
        [pharmacyPOS]
    );

    const closePaymentModal = useCallback(() => {
        setShowPaymentModal(false);
    }, []);

    const resetPaymentState = useCallback(() => {
        setPaymentData(INITIAL_PAYMENT_DATA(pharmacyPOS));
        setPaidType("full");
        setErrors({});
        resetDoctorPrescriptionMeta();
    }, [pharmacyPOS, resetDoctorPrescriptionMeta]);

    const handleSettlePayment = useCallback(
        (e) => {
            e.preventDefault();
            const isCurrentlySettle = paymentData.isSettle;
            const actionText = isCurrentlySettle
                ? t("Do you want to un-settle this payment?")
                : t("Do you want to settle this payment?");
            const confirmText = isCurrentlySettle ? t("Yes, Unsettle it!") : t("Yes, Settle it!");
            Swal.fire({
                title: t("Are you sure?"),
                text: actionText,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: confirmText,
                cancelButtonText: t("No, Cancel"),
            }).then((result) => {
                if (result.isConfirmed) {
                    setPaymentData((prev) => ({ ...prev, isSettle: !isCurrentlySettle }));
                }
            });
        },
        [paymentData.isSettle, t]
    );

    const buildDoctorPayload = useCallback(
        () => buildDoctorApiPayload(doctorForm),
        [doctorForm]
    );

    const applyResolvedDoctor = useCallback(
        ({ doctorId: resolvedId, doctorForm: resolvedForm, selectedDoctorOption: resolvedOption }) => {
            setDoctorForm(resolvedForm || EMPTY_DOCTOR_FORM);
            setDoctorId(resolvedId || null);
            setSelectedDoctorOption(resolvedOption || null);
            setDoctorFormVersion((prev) => prev + 1);
            setErrors((prev) => {
                const next = { ...prev };
                delete next.doctorName;
                delete next.doctorEmail;
                return next;
            });
        },
        []
    );

    const hydrateDoctorFromPrescription = useCallback(
        async (prescriptionDoctor) => {
            if (
                !prescriptionDoctor?.name?.trim() ||
                isPlaceholderDoctorName(prescriptionDoctor.name)
            ) {
                return null;
            }

            try {
                const resolved = await resolveDoctorFromPrescription(prescriptionDoctor);
                applyResolvedDoctor(resolved);
                return resolved;
            } catch (error) {
                console.error("Error resolving prescription doctor:", error);
                return null;
            }
        },
        [applyResolvedDoctor]
    );

    const handleAddDoctorToMaster = useCallback(async () => {
        const validationErrors = validateDoctorFormForCreate(doctorForm, t);

        if (Object.keys(validationErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...validationErrors }));
            return null;
        }

        setErrors((prev) => {
            const next = { ...prev };
            delete next.doctorName;
            delete next.doctorEmail;
            return next;
        });

        try {
            setAddingDoctor(true);
            const payload = buildDoctorPayload();
            const response = await createPharmaDoctorAPI(payload);
            const createdDoctor = extractCreatedDoctor(response);
            const newDoctorId = extractCreatedDoctorId(response);

            if (!newDoctorId) {
                toast.error(t("Doctor saved but ID was not returned. Please search again."));
                return null;
            }

            setDoctorId(newDoctorId);
            setSelectedDoctorOption(
                buildDoctorSelectOption({ ...createdDoctor, _id: newDoctorId, name: createdDoctor.name || payload.name })
            );
            toast.success(response?.data?.message || t("Doctor added successfully"));
            return newDoctorId;
        } catch (error) {
            console.error("Error creating doctor:", error);
            return null;
        } finally {
            setAddingDoctor(false);
        }
    }, [buildDoctorPayload, doctorForm, t]);

    const handlePaymentSave = useCallback(async () => {
        const validationErrors = {};
        if (!paymentData.type) validationErrors.type = t("Payment type is required");
        if (!paymentData.amount) validationErrors.amount = t("Amount is required");

        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return null;

        const dueInPayCurrency = Number(
            convertCurrency(totals.netTotal, currency, paymentData.currency)
        );
        const paidAmount = Number(paymentData.amount) || 0;
        if (isOverpayment(dueInPayCurrency, paidAmount)) {
            toast.error(t("Amount exceeds invoice total"));
            return null;
        }

        const effectivePaidType =
            paidType === "partial" && paidAmount < dueInPayCurrency - 0.01
                ? "partial"
                : "full";

        let resolvedDoctorId = doctorId;
        if (!resolvedDoctorId && sanitizeDoctorName(doctorForm?.name)) {
            try {
                resolvedDoctorId = await resolveDoctorIdForSale({
                    doctorId,
                    doctorForm,
                });
                if (resolvedDoctorId) {
                    setDoctorId(resolvedDoctorId);
                }
            } catch (error) {
                console.error("Error resolving doctor for sale:", error);
                toast.error(
                    error?.response?.data?.message ||
                        t("Could not link doctor to this sale. Please try again.")
                );
                return null;
            }
        }

        // Pass the isSameState flag to the payload builder
        const optionalSaleMeta = buildOptionalSaleMeta({
            doctorId: resolvedDoctorId,
            doctorForm,
            prescriptionFile,
            prescriptionDate,
        });

        const payload = buildInvoicePayloadWithTax({
            cart,
            itemTaxes,
            discountType,
            discountValue,
            transportCost,
            paymentData,
            paidType: effectivePaidType,
            customerType,
            selectedPatient,
            pharmacyPOS,
            posTaxType,
            pharmacyStateCode,
            patientStateCode,
            isSameState,
            convertCurrency,
            sourceCurrency: currency,
            ...optionalSaleMeta,
            t
        });

        setLoading(true);
        // const formData = buildSalesFormData(payload, prescriptionFile);
        // formData.forEach((key, value) => {
        //     console.log(value + ":" + key);

        // })

        try {
            const formData = buildSalesFormData(payload, prescriptionFile);

            const response = await addSales(formData);
            const data = response.data?.data?.sales;
            const receiptQrData = response.data?.data.paymentQR;

            toast.success(response.data?.message || t("Sale saved successfully"));

            // Create converted items with tax information
            const convertedItems = cart.map(item => {
                const itemKey = `${item._id}-${item.selectedBatchId}`;
                const taxInfo = itemTaxes[itemKey] || {};

                // Determine item-specific tax percentages based on isSameState
                let itemCGSTPercentage = 0;
                let itemSGSTPercentage = 0;
                let itemIGSTPercentage = 0;

                if (posTaxType === "gst") {
                    if (isSameState) {
                        // Intra-state: split the tax percentage equally
                        itemCGSTPercentage = (taxInfo.taxPercentage || 0) / 2;
                        itemSGSTPercentage = (taxInfo.taxPercentage || 0) / 2;
                    } else {
                        // Inter-state: full percentage for IGST
                        itemIGSTPercentage = taxInfo.taxPercentage || 0;
                    }
                }

                return {
                    ...item,
                    salePrice: convertCurrency(item.salePrice, currency, paymentData.currency),
                    // Add tax information to each item
                    tax: convertCurrency(taxInfo.tax || 0, currency, paymentData.currency),
                    taxPercentage: taxInfo.taxPercentage || 0,
                    CGST: convertCurrency(taxInfo.CGST || 0, currency, paymentData.currency),
                    SGST: convertCurrency(taxInfo.SGST || 0, currency, paymentData.currency),
                    IGST: convertCurrency(taxInfo.IGST || 0, currency, paymentData.currency),
                    VAT: convertCurrency(taxInfo.VAT || 0, currency, paymentData.currency),
                    CGSTPercentage: itemCGSTPercentage,
                    SGSTPercentage: itemSGSTPercentage,
                    IGSTPercentage: itemIGSTPercentage,
                    isIGST: !isSameState && posTaxType === "gst"
                };
            });

            // Calculate totals for invoice display (converted)
            const convertedTax = convertCurrency(totals.tax, currency, paymentData.currency);
            const convertedCGST = convertCurrency(totals.CGST, currency, paymentData.currency);
            const convertedSGST = convertCurrency(totals.SGST, currency, paymentData.currency);
            const convertedIGST = convertCurrency(totals.IGST, currency, paymentData.currency);
            const convertedVAT = convertCurrency(totals.VAT, currency, paymentData.currency);
            const convertedSubtotal = convertCurrency(totals.subtotal, currency, paymentData.currency);
            const convertedDiscount = convertCurrency(totals.discount, currency, paymentData.currency);
            const convertedNetTotal = convertCurrency(totals.netTotal, currency, paymentData.currency);

            // Get the tax percentages for the invoice display
            const maxTaxPercentage = Math.max(...Object.values(itemTaxes || {}).map(t => t.taxPercentage || 0), 0);

            // Calculate CGST/SGST/IGST percentages based on the tax type
            let CGSTPercentage = 0;
            let SGSTPercentage = 0;
            let IGSTPercentage = 0;

            if (posTaxType === "gst") {
                if (isSameState) {
                    // Intra-state: split the tax percentage equally
                    CGSTPercentage = maxTaxPercentage / 2;
                    SGSTPercentage = maxTaxPercentage / 2;
                } else {
                    // Inter-state: full percentage for IGST
                    IGSTPercentage = maxTaxPercentage;
                }
            }

            const prescribedDoctor = buildInvoiceDoctorInfo(doctorForm);

            const invoice = {
                date: new Date().toISOString(),
                pharmacy: pharmacyPOS?.facility_name,
                email: pharmacyPOS?.email,
                phone: `${pharmacyPOS?.contact_person?.phone_code || ""} ${pharmacyPOS?.contact_person?.mobile || ""}`.trim(),
                customer: paymentData.patientName || t("Walk-in Customer"),
                ...(prescribedDoctor ? { prescribedDoctor } : {}),
                items: convertedItems,

                // Use converted values
                subtotal: convertedSubtotal,
                discount: convertedDiscount,
                tax: convertedTax,
                CGST: convertedCGST,
                SGST: convertedSGST,
                IGST: convertedIGST,
                VAT: convertedVAT,
                total: Number(convertedNetTotal),
                paid: Number(paymentData.amount),

                // Tax metadata
                taxType: posTaxType.toUpperCase(),
                taxPercentage: maxTaxPercentage,
                CGSTPercentage,
                SGSTPercentage,
                IGSTPercentage,

                // Tax type indicator
                isSameState,
                isIGST: !isSameState && posTaxType === "gst",

                invoiceNo: data?.salesNumber,

                // Currency info
                currency: paymentData.currency,
                currencySymbol: getCurrencySymbol(paymentData.currency),

                discountMeta: { type: discountType, value: discountValue },
                receiptQrData,

                // State info for display
                pharmacyStateCode,
                patientStateCode,

                // Payment info
                paymentType: paymentData.type,

                // Tax summary based on state
                taxSummary: {
                    isSameState,
                    taxType: posTaxType,
                    intraStateTax: isSameState ? "CGST + SGST" : "N/A",
                    interStateTax: !isSameState ? "IGST" : "N/A",
                    pharmacyState: pharmacyStateCode || "Not set",
                    patientState: patientStateCode || "Not set"
                }
            };

            setInvoiceData(invoice);
            setShowInvoice(true);
            setShowPaymentModal(false);
            return invoice;
        } catch (error) {
            console.error("Error saving sales:", error);
            // toast.warning(
            //     error.response?.data?.message || t("Failed to save sale. Please try again later.")
            // );
            return null;
        } finally {
            setLoading(false);
        }
    }, [
        cart,
        itemTaxes,
        customerType,
        discountType,
        discountValue,
        getCurrencySymbol,
        paidType,
        paymentData,
        pharmacyPOS,
        setLoading,
        t,
        transportCost,
        totals,
        convertCurrency,
        currency,
        posTaxType,
        pharmacyStateCode,
        patientStateCode,
        isSameState,
        prescriptionFile,
        prescriptionDate,
        doctorForm,
        doctorId
    ]);

    const closeInvoice = useCallback(() => {
        setShowInvoice(false);
    }, []);

    return {
        paymentData,
        setPaymentData,
        paidType,
        setPaidType,
        showPaymentModal,
        openPaymentModal,
        closePaymentModal,
        handleSettlePayment,
        handlePaymentSave,
        errors,
        showInvoice,
        invoiceData,
        closeInvoice,
        resetPaymentState,
        totals,
        isSameState,
        prescriptionDate,
        setPrescriptionDate,
        doctorForm,
        updateDoctorForm,
        clearDoctorSelection,
        doctorId,
        setDoctorId,
        selectedDoctorOption,
        setSelectedDoctorOption,
        handleAddDoctorToMaster,
        addingDoctor,
        doctorFormVersion,
        resetDoctorPrescriptionMeta,
        hydrateDoctorFromPrescription,
    };
};